// The live event stream, kept open (QA-058). The browser reconnects an EventSource itself after a
// dropped connection, but gives up for good on an error answer (a proxy's 502). This reopens it,
// with a short backoff, from the last event seen, so the server replays whatever changed while it
// was away and every screen catches up through its own change handler.
import { reportConnection } from './connection.ts';

type Listener = (e: MessageEvent<string>) => void;
const STEPS_MS = [1000, 2000, 3000];

export interface LiveStream {
  addEventListener(type: 'change' | 'open', fn: Listener): void;
  close(): void;
}

/**
 * Whether this session may open the stream (QA-397: only a role that sees every job). Off, every
 * screen's liveStream() is a quiet one that never connects, so it never meets a 403 or retries one;
 * those screens refresh on their own actions or a reload. Set from /auth/session at sign-in.
 */
let allowed = false;
export function allowLiveUpdates(on: boolean): void {
  allowed = on;
}

const QUIET: LiveStream = { addEventListener: () => undefined, close: () => undefined };

export function liveStream(url = '/events/stream'): LiveStream {
  if (!allowed) return QUIET;
  const listeners = new Map<string, Set<Listener>>();
  let es: EventSource | null = null;
  let last = '';
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  const emit = (type: string, e: MessageEvent<string>) => {
    for (const fn of listeners.get(type) ?? []) fn(e);
  };
  const open = () => {
    if (closed) return;
    es = new EventSource(last ? `${url}?after=${encodeURIComponent(last)}` : url);
    es.addEventListener('open', (e) => {
      attempt = 0;
      reportConnection(true);
      emit('open', e as MessageEvent<string>);
    });
    es.addEventListener('change', (e) => {
      const m = e as MessageEvent<string>;
      if (m.lastEventId) last = m.lastEventId;
      emit('change', m);
    });
    es.addEventListener('error', () => {
      if (!es || es.readyState !== EventSource.CLOSED) return; // the browser is already retrying
      reportConnection(false);
      es = null;
      timer = setTimeout(open, STEPS_MS[Math.min(attempt++, STEPS_MS.length - 1)]);
    });
  };
  open();
  return {
    addEventListener(type, fn) {
      if (!listeners.has(type)) listeners.set(type, new Set());
      listeners.get(type)!.add(fn);
    },
    close() {
      closed = true;
      clearTimeout(timer);
      es?.close();
    },
  };
}

/** One change as the stream says it: what happened, and to which record. */
export interface LiveChange {
  type: string;
  entityType?: string | null;
  entityId?: string | null;
  /** The job the change is on, when the stream names one (a job page follows only its own). */
  jobId?: string | null;
}

// The shell's own stream, shared (QA-726, QA-728): the office shell hears a ringing call on it and the
// sheets over every place follow their record on it, on one connection, not one each.
let shared: { stream: LiveStream; fns: Set<(c: LiveChange) => void> } | null = null;

/**
 * Hear every change on the one shared stream; the returned function stops. The stream opens with the
 * first listener and closes with the last, so a sign-out (the shell's listener gone) closes it.
 */
export function onLiveChange(fn: (c: LiveChange) => void): () => void {
  if (!shared) {
    const fns = new Set<(c: LiveChange) => void>();
    const stream = liveStream();
    stream.addEventListener('change', (e) => {
      let c: LiveChange;
      try {
        c = JSON.parse(e.data) as LiveChange;
      } catch {
        return;
      }
      for (const f of [...fns]) f(c);
    });
    shared = { stream, fns };
  }
  const s = shared;
  s.fns.add(fn);
  return () => {
    s.fns.delete(fn);
    if (!s.fns.size && shared === s) {
      s.stream.close();
      shared = null;
    }
  };
}
